// Plan 24c, Task 8 — Game Over screen (Screen 25)
//
// Full-viewport overlay shown at game end. Two-column layout:
//   Left column  — DOM anchor for Plan 13's Three.js 3D podium wiring.
//   Right column — Panel with GAME OVER title, winner name (pulsed), final
//                  standings (sorted), Play Again + Back to Warmup buttons.
// Auto-returns to warmup after autoMenuSeconds (default 30).

import { createScoreboardRow } from '@client/ui/components/scoreboard-row';
import type { ScoreboardRowHandle } from '@client/ui/components/scoreboard-row';
import { createButton } from '@client/ui/components/button';
import type { UIComponentHandle } from '@client/ui/components/types';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type GameOverPayload = BoardMountPayload & {
  winnerPlayerId: string | null;
  autoMenuSeconds?: number;
  onPlayAgain?: () => void;
  onBackToMenu?: () => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

const Z_INDEX_OVERLAY = 350;
const OVERLAY_BG = 'rgba(0, 0, 0, 0.60)';
const PANEL_BG = '#1E2332';
const RADIUS_LG = '12px';
const SHADOW_PANEL = '0 4px 12px rgba(0,0,0,0.50)';
const PANEL_PADDING = '24px 28px';
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";
const TITLE_SHADOW = '0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)';
const H1_SIZE = '48px';
const H2_SIZE = '36px';
const H4_SIZE = '22px';
const COLOR_PRIMARY = '#FFFFFF';
const COLOR_FIRST_GOLD = '#FFD700';
const COLOR_TEXT_SECONDARY = '#B0B8C8';
const SPACING_LG = '24px';
const PANEL_WIDTH = '420px';

const MAX_HP = 30;
const DEFAULT_AUTO_MENU_SECONDS = 30;

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createGameOverScreen(ctx: ScreenMountContext<GameOverPayload>): ScreenHandle {
  const { host, gsapRegistry, router, payload } = ctx;
  const { state, winnerPlayerId, onPlayAgain, onBackToMenu } = payload;
  const autoMenuSeconds = payload.autoMenuSeconds ?? DEFAULT_AUTO_MENU_SECONDS;

  let disposed = false;

  // Collected handles for disposal
  const rowHandles: ScoreboardRowHandle[] = [];
  const staggerHandles: Array<ReturnType<typeof gsapRegistry.create>> = [];
  let playAgainHandle: UIComponentHandle | null = null;
  let backToMenuHandle: UIComponentHandle | null = null;

  // ── Default navigation helpers ─────────────────────────────────────────────

  const defaultBackToMenu = (): void => {
    router.navigate('warmup-arena-overlay', { portal: null });
  };

  // ── Root overlay ───────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-game-over';
  root.setAttribute('data-screen-id', 'game-over');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: ${Z_INDEX_OVERLAY}`,
    `background: ${OVERLAY_BG}`,
    'display: flex',
    'align-items: center',
    'justify-content: center',
    `gap: ${SPACING_LG}`,
    'pointer-events: auto',
  ].join('; ');

  // ── Left column — 3D podium anchor ────────────────────────────────────────

  const leftCol = document.createElement('div');
  leftCol.style.cssText = [
    'flex: 1',
    'display: flex',
    'align-items: center',
    'justify-content: center',
  ].join('; ');

  const podiumAnchor = document.createElement('div');
  podiumAnchor.className = 'game-over__podium-anchor';
  podiumAnchor.setAttribute('data-3d-podium', '');
  podiumAnchor.style.cssText = ['width: 100%', 'min-height: 400px'].join('; ');

  leftCol.appendChild(podiumAnchor);
  root.appendChild(leftCol);

  // ── Right column — panel ───────────────────────────────────────────────────

  const rightCol = document.createElement('div');
  rightCol.style.cssText = [`width: ${PANEL_WIDTH}`, 'flex-shrink: 0'].join('; ');

  const panel = document.createElement('div');
  panel.className = 'game-over-panel';
  panel.style.cssText = [
    `background: ${PANEL_BG}`,
    `border-radius: ${RADIUS_LG}`,
    `box-shadow: ${SHADOW_PANEL}`,
    `padding: ${PANEL_PADDING}`,
    'display: flex',
    'flex-direction: column',
    'gap: 16px',
  ].join('; ');

  // ── Title: "GAME OVER" ─────────────────────────────────────────────────────

  const titleEl = document.createElement('h1');
  titleEl.className = 'game-over-title';
  titleEl.textContent = 'GAME OVER';
  titleEl.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${H1_SIZE}`,
    `color: ${COLOR_PRIMARY}`,
    `text-shadow: ${TITLE_SHADOW}`,
    'margin: 0',
    'text-align: center',
    'line-height: 1',
  ].join('; ');

  panel.appendChild(titleEl);

  // ── Winner name ────────────────────────────────────────────────────────────

  const winner = winnerPlayerId == null ? null : state.players.get(winnerPlayerId);
  const winnerName = winner?.name ?? 'No Winner';

  const winnerEl = document.createElement('h2');
  winnerEl.className = 'game-over-winner';
  winnerEl.textContent = winner == null ? winnerName : `${winnerName} Wins!`;
  winnerEl.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${H2_SIZE}`,
    `color: ${COLOR_FIRST_GOLD}`,
    'margin: 0',
    'text-align: center',
    'line-height: 1',
  ].join('; ');

  panel.appendChild(winnerEl);

  // Winner pulse animation
  const pulseHandle = gsapRegistry.create('game-over:winner-pulse');
  pulseHandle.timeline.to(winnerEl, { scale: 1.04, repeat: -1, yoyo: true, duration: 0.8 });

  // ── Final Standings subtitle ───────────────────────────────────────────────

  const standingsTitle = document.createElement('h4');
  standingsTitle.className = 'game-over-standings-title';
  standingsTitle.textContent = 'Final Standings';
  standingsTitle.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${H4_SIZE}`,
    `color: ${COLOR_TEXT_SECONDARY}`,
    'margin: 0',
  ].join('; ');

  panel.appendChild(standingsTitle);

  // ── Sorted standings ───────────────────────────────────────────────────────

  const sorted =
    state.endGame?.rankings
      .map((ranking) => ({ rank: ranking.rank, player: state.players.get(ranking.playerId) }))
      .filter(
        (entry): entry is { rank: number; player: NonNullable<typeof entry.player> } =>
          entry.player != null,
      ) ??
    [...state.turnOrder]
      .map((id, index) => ({ rank: index + 1, player: state.players.get(id)! }))
      .filter(({ player }) => player != null)
      .sort((a, b) => {
        const trophyDiff = b.player.trophies - a.player.trophies;
        if (trophyDiff !== 0) return trophyDiff;
        return b.player.keys - a.player.keys;
      })
      .map((entry, index) => ({ ...entry, rank: index + 1 }));

  const rowsContainer = document.createElement('div');
  rowsContainer.className = 'game-over-rows';
  rowsContainer.style.cssText = ['display: flex', 'flex-direction: column', 'gap: 4px'].join('; ');

  for (let i = 0; i < sorted.length; i++) {
    const { player, rank } = sorted[i];

    const rowWrapper = document.createElement('div');
    rowWrapper.className = 'game-over-row-wrapper';

    const rowHandle = createScoreboardRow({
      gsapRegistry,
      mount: rowWrapper,
      playerName: player.name,
      playerColor: `#${player.color.toString(16).padStart(6, '0')}`,
      currentHP: player.hp,
      maxHP: MAX_HP,
      keys: player.keys,
      trophies: player.trophies,
      rank,
    });

    rowHandles.push(rowHandle);

    // Stagger entrance animation
    const tlHandle = gsapRegistry.create('game-over:row-' + i);
    tlHandle.timeline.fromTo(
      rowWrapper,
      { x: -20, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.3 },
      i * 0.04,
    );
    staggerHandles.push(tlHandle);

    rowsContainer.appendChild(rowWrapper);
  }

  panel.appendChild(rowsContainer);

  // ── Action buttons ─────────────────────────────────────────────────────────

  const btnRow = document.createElement('div');
  btnRow.style.cssText = ['display: flex', `gap: ${SPACING_LG}`].join('; ');

  playAgainHandle = createButton({
    gsapRegistry,
    mount: btnRow,
    variant: 'primary',
    size: 'large',
    label: 'Play Again',
    onClick: () => {
      if (!disposed) {
        if (onPlayAgain) {
          onPlayAgain();
        } else {
          router.navigate('lobby-host', payload);
        }
      }
    },
  });

  backToMenuHandle = createButton({
    gsapRegistry,
    mount: btnRow,
    variant: 'ghost',
    size: 'large',
    label: 'Back to Warmup',
    onClick: () => {
      if (!disposed) {
        if (onBackToMenu) {
          onBackToMenu();
        } else {
          defaultBackToMenu();
        }
      }
    },
  });

  panel.appendChild(btnRow);

  // ── Auto-return timer ──────────────────────────────────────────────────────

  const autoHandle = gsapRegistry.create('game-over:auto-menu');
  autoHandle.timeline.call(
    () => {
      if (disposed) return;
      if (onBackToMenu) {
        onBackToMenu();
      } else {
        defaultBackToMenu();
      }
    },
    undefined,
    autoMenuSeconds,
  );

  // ── ESC handler ────────────────────────────────────────────────────────────

  function handleEsc(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (!disposed) {
      if (onBackToMenu) {
        onBackToMenu();
      } else {
        defaultBackToMenu();
      }
    }
  }

  document.addEventListener('keydown', handleEsc);

  // ── Mount ──────────────────────────────────────────────────────────────────

  rightCol.appendChild(panel);
  root.appendChild(rightCol);
  host.appendChild(root);

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Kill auto-return and pulse timelines
    autoHandle.dispose();
    pulseHandle.dispose();

    // Kill stagger entrance handles
    for (const tlHandle of staggerHandles) {
      tlHandle.dispose();
    }

    // Dispose row handles
    for (const rowHandle of rowHandles) {
      rowHandle.dispose();
    }

    // Dispose buttons
    if (playAgainHandle) {
      playAgainHandle.dispose();
    }
    if (backToMenuHandle) {
      backToMenuHandle.dispose();
    }

    // Remove ESC listener
    document.removeEventListener('keydown', handleEsc);

    // Remove from DOM
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
